/**
 * Trial modal state machine (0.8.5 工单 08). Pure derivations shared by the
 * modal and its unit tests: the phase comes from the single eligibility
 * payload, and the two buttons are gated on it.
 *
 *   signIn     → anonymous: sign-in CTA (carries `next` back here)
 *   claim      → eligible: "领取试用"
 *   claimed    → a claim just succeeded (copy shows the end date) — the +7 bind
 *                button only appears once the refreshed subscription lands
 *   bind       → an active unbound trial that has not ended: "+7 days with
 *                PayPal"
 *   ineligible → already claimed / paid
 *
 * The bind button is additionally gated on the offer advertising PayPal binding
 * support AND PayPal being configured (`bindingReady`), and on a concrete
 * `subscriptionId`: without it the bind call is a no-op, so the button must not
 * render as a dead control (neither before the claim nor during the refresh
 * window after it).
 */

import type { TrialState } from "@lib/data/trial"
import { DISCORD_INVITE_URL } from "@lib/util/env"

export type TrialPhase = "signIn" | "claim" | "claimed" | "bind" | "ineligible"

export const deriveTrialPhase = (
  state: TrialState | null,
  { claimedEndsAt, now }: { claimedEndsAt: string | null; now: number }
): TrialPhase => {
  if (!state || !state.authenticated) return "signIn"
  if (claimedEndsAt) return "claimed"
  const sub = state.subscription
  const notEnded = !sub?.trialEndsAt || new Date(sub.trialEndsAt).getTime() > now
  if (sub && !sub.hasPaymentMethod && notEnded) return "bind"
  if (state.eligible) return "claim"
  return "ineligible"
}

export const canShowClaimButton = (phase: TrialPhase): boolean =>
  phase === "claim"

/**
 * T06 (2026-10-02 walkthrough): the modal's right-hand button binds, and in the
 * claim phase it chains a claim first — so unlike `canShowBindButton` it does
 * not need a subscription id up front (the claim response carries it), only
 * PayPal being ready and the offer advertising binding support. No dead
 * control: when either is missing the button is not rendered at all.
 */
export const canShowBindAndClaimButton = (
  state: TrialState | null,
  phase: TrialPhase
): boolean => {
  if (phase !== "claim" && phase !== "claimed" && phase !== "bind") {
    return false
  }
  return state?.offer?.bindingSupported === true && state.bindingReady === true
}

export const canShowBindButton = (
  state: TrialState | null,
  phase: TrialPhase
): boolean => {
  if (phase !== "claimed" && phase !== "bind") return false
  if (state?.offer?.bindingSupported !== true || state.bindingReady !== true) {
    return false
  }
  const subscriptionId = state.subscription?.subscriptionId
  return typeof subscriptionId === "string" && subscriptionId.length > 0
}

/**
 * Whether the floating acquisition bar has something to offer (2026-10-02
 * walkthrough, T05). Anonymous visitors keep the bar - a product decision -
 * but a signed-in visitor sees it only while a claim or a bind is actually
 * available, so a bound / paid / ended account stops being told to claim.
 */
export const floatingBannerVisible = (state: TrialState | null): boolean => {
  if (!state || !state.authenticated) return true
  if (state.eligible) return true
  const sub = state.subscription
  const notEnded = !sub?.trialEndsAt || new Date(sub.trialEndsAt).getTime() > Date.now()
  return Boolean(sub && !sub.hasPaymentMethod && notEnded)
}

/**
 * T13（2026-10-02 走查）：Discord 服务器门槛的拦截态。
 *
 * 只有 `not_member` / `no_discord_account` 会拦领取（去入服），`reauth_required`
 * 要求重新用 Discord 登录一次（旧 token 没有 `guilds` scope）；`unavailable` /
 * `gate_off` / 缺失**故意不拦**——门槛是营销条件，上游抖动不该把已入服的付费
 * 顾客挡在门外（引擎侧另有权威校验）。
 */
export type TrialGateBlock = "discord_required" | "discord_reauth" | null

export const trialGateBlock = (state: TrialState | null): TrialGateBlock => {
  const gate = state?.discord
  if (!gate) return null
  if (gate.status === "not_member" || gate.status === "no_discord_account") {
    return "discord_required"
  }
  if (gate.status === "reauth_required") return "discord_reauth"
  return null
}

/**
 * T02 (2026-10-02 walkthrough): the invite link the gate CTA points at. The
 * plugin config now ships it with the eligibility payload, so it can change
 * without a storefront rebuild; the build-time env var stays as a fallback for
 * a deployment that has not moved it into the plugin yet (or an older plugin
 * that does not send the field).
 */
export const resolveDiscordInviteUrl = (
  gate: TrialState["discord"] | null | undefined
): string => gate?.inviteUrl || DISCORD_INVITE_URL
